// src/Pages/DashboardAssistant/DashboardCanvasFlexible.jsx
import React from "react";
import DashboardTileDragable from "./DashboardTileDragable";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  createSnapModifier,
} from "@dnd-kit/modifiers";

const SNAP_SIZE = 30;
const DEFAULT_WIDTH = 320;
const DEFAULT_HEIGHT = 240;
const GUTTER = 8;

const snap = (v) => Math.round(v / SNAP_SIZE) * SNAP_SIZE;

const getRect = (tile) => {
  const p = tile.position ?? {};
  return {
    x: p.x ?? 0,
    y: p.y ?? 0,
    width: Math.max(p.width ?? DEFAULT_WIDTH, SNAP_SIZE),
    height: Math.max(p.height ?? DEFAULT_HEIGHT, SNAP_SIZE),
  };
};

const boxesOverlap = (a, b) => {
  return !(
    a.x + a.width <= b.x ||
    b.x + b.width <= a.x ||
    a.y + a.height <= b.y ||
    b.y + b.height <= a.y
  );
};

const resolveOverlaps = (tiles) => {
  const resolved = [];
  const sorted = [...tiles].sort((a, b) => {
    const ra = a.position?.y ?? 0;
    const rb = b.position?.y ?? 0;
    if (ra !== rb) return ra - rb;
    const ca = a.position?.x ?? 0;
    const cb = b.position?.x ?? 0;
    return ca - cb;
  });

  for (const tile of sorted) {
    const rect = getRect(tile);
    let next = { ...rect, x: snap(rect.x), y: snap(rect.y) };

    let guard = 0;
    let collision = resolved.find((r) => boxesOverlap(next, getRect(r)));
    while (collision && guard < 200) {
      // push down below the collision and try again, snapping to grid
      const bumpedY = snap(collision.position.y + (collision.position.height ?? next.height) + GUTTER);
      next.y = bumpedY;
      // if (bounds) {
      //   const maxY = Math.max(0, (bounds.height ?? next.y + next.height) - next.height);
      //   next.y = clamp(next.y, 0, maxY);
      // }
      collision = resolved.find((r) => boxesOverlap(next, getRect(r)));
      guard += 1;
    }

    resolved.push({
      ...tile,
      position: {
        ...tile.position,
        x: next.x,
        y: next.y,
        width: next.width,
        height: next.height,
      },
    });
  }

  return resolved;
};

const DashboardCanvasFlexible = ({
  tiles,
  setTiles,
  removeTile,
  layout,
  setLayout,
  readOnly,
  onEditTile,
  gridRefExternal,
  showGrid = true,
}) => {
  const gridRef = React.useRef(null);
  const canvasWidth = layout?.width ?? 1600;
  const canvasHeight = layout?.height ?? 900;
  React.useEffect(() => {
    if (gridRefExternal) {
      gridRefExternal.current = gridRef.current;
      return () => {
        gridRefExternal.current = null;
      };
    }
  }, [gridRefExternal]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const snapModifier = React.useMemo(
    () => createSnapModifier(SNAP_SIZE),
    []
  );

  const applyAndResolve = (updater, minSize) => {
    setTiles((prev) => {
      const nextList = typeof updater === "function" ? updater(prev) : updater;
      const resolved = resolveOverlaps(nextList);
      const maxX = Math.max(
        0,
        ...resolved.map((t) => (t.position?.x ?? 0) + (t.position?.width ?? DEFAULT_WIDTH))
      );
      const maxY = Math.max(
        0,
        ...resolved.map((t) => (t.position?.y ?? 0) + (t.position?.height ?? DEFAULT_HEIGHT))
      );
      if (setLayout) {
        setLayout((prevLayout) => ({
          ...(prevLayout ?? {}),
          width: canvasWidth,
          height: canvasHeight,
        }));
      }
      return resolved;
    });
  };

  const handleDragEnd = (evt) => {
    const { active, delta } = evt;
    const dx = delta?.x ?? 0;
    const dy = delta?.y ?? 0;
    const tile = tiles.find((t) => t.id === active.id);
    const tileW = tile?.position?.width ?? DEFAULT_WIDTH;
    const tileH = tile?.position?.height ?? DEFAULT_HEIGHT;
    const targetX = snap((tile?.position?.x ?? 0) + dx);
    const targetY = snap((tile?.position?.y ?? 0) + dy);
    const clampedX = Math.max(0, Math.min(targetX, canvasWidth - tileW));
    const clampedY = Math.max(0, Math.min(targetY, canvasHeight - tileH));

    applyAndResolve((prev) =>
      prev.map((t) => {
        if (t.id !== active.id) return t;
        const baseX = t.position?.x ?? 0;
        const baseY = t.position?.y ?? 0;
        return {
          ...t,
          position: {
            ...t.position,
            x: clampedX,
            y: clampedY,
          },
        };
      }),
      { width: canvasWidth, height: canvasHeight }
    );
  };

  const handleResize = (id, nextPosition) => {
    applyAndResolve(
      (prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                position: {
                  ...t.position,
                  ...nextPosition,
                  width: Math.min(
                    nextPosition.width ?? DEFAULT_WIDTH,
                    canvasWidth - (nextPosition.x ?? 0)
                  ),
                  height: Math.min(
                    nextPosition.height ?? DEFAULT_HEIGHT,
                    canvasHeight - (nextPosition.y ?? 0)
                  ),
                },
              }
            : t
        ),
      { width: canvasWidth, height: canvasHeight }
    );
  };

  const ensurePositions = React.useCallback((list) => {
    let changed = false;
    const next = list.map((t, idx) => {
      const hasPos = t.position && ("x" in t.position || "y" in t.position);
      if (hasPos) return t;
      changed = true;
      const row = Math.floor(idx / 3);
      const col = idx % 3;
      return {
        ...t,
        position: {
          x: col * (DEFAULT_WIDTH + GUTTER),
          y: row * (DEFAULT_HEIGHT + GUTTER),
          width: DEFAULT_WIDTH,
          height: DEFAULT_HEIGHT,
        },
      };
    });
    return { next, changed };
  }, []);

  React.useEffect(() => {
    if (!tiles?.length) return;
    const { next, changed } = ensurePositions(tiles);
    if (changed && !readOnly) applyAndResolve(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiles, ensurePositions, readOnly]);

  const handleUpdateTile = (id, patch) => {
    if (patch?.__editContent && onEditTile) {
      const tile = tiles.find((t) => t.id === id);
      onEditTile(tile);
      return;
    }
    if (!setTiles) return;
    setTiles((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  };

  return (
    <div className="da-canvas">
      <div
        className="da-grid freeform"
        ref={gridRef}
        style={{
          position: "relative",
          width: `${canvasWidth}px`,
          height: "auto",
          minHeight: `${canvasHeight}px`,
          backgroundSize: `${SNAP_SIZE}px ${SNAP_SIZE}px`,
          backgroundImage: showGrid
            ? "linear-gradient(to right, rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.03) 1px, transparent 1px)"
            : "none",
        }}
      >
        {readOnly ? (
          tiles.map((tile) => (
            <DashboardTileDragable
              key={tile.id}
              tile={tile}
              removeTile={undefined}
              onResize={undefined}
              gridRef={gridRef}
              snapSize={SNAP_SIZE}
              readOnly
              onUpdateTile={undefined}
            />
          ))
        ) : (
          <DndContext
            sensors={sensors}
            modifiers={[snapModifier]}
            onDragEnd={handleDragEnd}
          >
            {tiles.map((tile) => (
              <DashboardTileDragable
                key={tile.id}
                tile={tile}
                removeTile={removeTile}
                onResize={handleResize}
                gridRef={gridRef}
                snapSize={SNAP_SIZE}
                onUpdateTile={handleUpdateTile}
              />
            ))}
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default DashboardCanvasFlexible;
